import { useState } from "react";
import { Loader, Modal } from "semantic-ui-react";

import { useSelectedDealer } from "components";
import { usePermanentRealTimeUser } from "hooks";
import { createFormHelper } from "lib/Form";
import { WEBHOOK_TRIGGERS } from "models";
import { CrudWebhookForm } from "modules/WebhooksDashboard/components/CrudWebhookModal/components";
import "modules/WebhooksDashboard/components/CrudWebhookModal/CrudWebhookModal.scss";
import { useCreateWebhookMutation, useEditWebhookMutation } from "modules/WebhooksDashboard/components/CrudWebhookModal/hooks";
import { useWebhooksQuery } from "modules/WebhooksDashboard/hooks";

import type { Checklist } from "models";
import type { CreateWebhookPayload } from "modules/WebhooksDashboard/components/CrudWebhookModal/hooks";

type LocationChecklist = {
  id: number;
  checklists: Checklist[];
};

type CrudWebhookModalProps = {
  isOpen: boolean;
  webhookId: number | null;
  locationChecklists: LocationChecklist[];
  isFetchingChecklists: boolean;
  onClose: () => void;
};

const FormHelper = createFormHelper<CreateWebhookPayload>();

export const CrudWebhookModal = ({ isOpen, onClose, webhookId, isFetchingChecklists, locationChecklists }: CrudWebhookModalProps) => {
  const [hasErrors, setHasErrors] = useState(false);

  const { user } = usePermanentRealTimeUser();
  const { data: webhooks } = useWebhooksQuery();
  const { selectedDealer } = useSelectedDealer();
  const { mutateAsync: editWebhook, isPending: isEditWebhookPending } = useEditWebhookMutation();
  const { mutateAsync: createWebhook, isPending: isCreateWebhookPending } = useCreateWebhookMutation();

  const webhook = webhooks?.find(webhook => webhook.id === webhookId);

  const defaultFormValues: CreateWebhookPayload =
    webhookId === -1 || !webhook
      ? {
          active: false,
          checklist_id: null,
          dealer_location_id: null,
          name: "",
          queued: false,
          url: "",
          webhook_trigger_id: null,
          block_creating_support_ticket: false,
          id: -1,
          dealer_id: selectedDealer?.id || 0,
          user_id: user?.id || 0
        }
      : {
          active: webhook.active,
          checklist_id: webhook.checklist_id,
          dealer_location_id: webhook.dealer_location_id,
          name: webhook.name,
          queued: webhook.queued,
          url: webhook.url,
          webhook_trigger_id: webhook.webhook_trigger_id,
          block_creating_support_ticket: webhook.block_creating_support_ticket,
          id: webhook.id,
          dealer_id: webhook.dealer_id || 0,
          user_id: webhook.user_id || 0,
          headers: webhook.headers?.map(({ name, value, id, webhook_id, created_on, updated_on }) => ({ name, value, id, webhook_id, created_on, updated_on })) || []
        };

  const handleClose = () => {
    onClose();
    setHasErrors(false);
  };

  const validateFormData = (values: CreateWebhookPayload) => {
    const hasErrors = !values.name || !values.webhook_trigger_id || !values.url;
    setHasErrors(hasErrors);
    return hasErrors;
  };

  const handleSubmit = async (values: CreateWebhookPayload) => {
    if (validateFormData(values)) return;

    if (webhookId === -1) await createWebhook({ ...values, user_id: user?.id || 0, dealer_id: selectedDealer?.id || 0 });
    else await editWebhook({ ...values, headers: values.headers || [], webhook_trigger_id: values.webhook_trigger_id || WEBHOOK_TRIGGERS.CarInShop });

    handleClose();
  };

  if (isFetchingChecklists) return <Loader active inline="centered" />;

  return (
    <Modal open={isOpen} size="large">
      <FormHelper.Form onSubmit={handleSubmit} defaultValues={defaultFormValues}>
        <div className="CrudWebhookModal">
          <CrudWebhookForm
            hasErrors={hasErrors}
            isPending={isCreateWebhookPending || isEditWebhookPending}
            onClose={handleClose}
            onSubmit={handleSubmit}
            locationChecklists={locationChecklists}
            webhookId={webhookId}
          />
        </div>
      </FormHelper.Form>
    </Modal>
  );
};
